import { Suspense, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuthStore, getAndClearReturnUrl } from "@/lib/stores/auth";
import { t } from "@/lib/typography";

function LoginContent() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { loginWithWhop, logout, user, isAuthenticated, isLoading } = useAuthStore();

  // Get returnUrl and upgrade flag from query params
  const returnUrl = searchParams.get("returnUrl");
  // upgrade=true means a free user needs to upgrade to paid auth
  // This prevents auto-redirect and ensures we clear free cookies before OAuth
  const isUpgrade = searchParams.get("upgrade") === "true";

  // Note: fetchUser is handled by AuthProvider on app mount

  // If already authenticated, redirect to return URL or dashboard
  // Skip auto-redirect when upgrading from free to paid auth
  useEffect(() => {
    if (!isLoading && isAuthenticated && !isUpgrade) {
      const storedReturnUrl = getAndClearReturnUrl();
      navigate(storedReturnUrl || returnUrl || "/dashboard");
    }
  }, [isLoading, isAuthenticated, isUpgrade, navigate, returnUrl]);

  const handleLogin = async () => {
    // If upgrading from free user, clear existing cookies first
    // This prevents the free user session from persisting after Whop OAuth
    if (isUpgrade && isAuthenticated) {
      await logout();
    }
    // Pass the returnUrl to be stored before OAuth redirect
    loginWithWhop(returnUrl || undefined);
  };

  if (isLoading) {
    return (
      <div className="flex min-h-[calc(100vh-8rem)] items-center justify-center">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  return (
    <div className="flex min-h-[calc(100vh-8rem)] flex-col items-center justify-center">
      <div className="w-full max-w-sm space-y-8 text-center">
        <div className="space-y-2">
          <h1 className={t.pageTitle}>Login</h1>
          <p className="text-muted-foreground">
            Sign in to access your assessments
          </p>
        </div>

        {isUpgrade && user?.type === "free" && (
          <div className="p-4 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 text-left">
            <p className="font-medium text-amber-800 dark:text-amber-200">
              Paid login required
            </p>
            <p className="text-xs text-amber-700 dark:text-amber-300 mt-1">
              This assessment is only available to paid members. Please sign in with Whop to continue.
            </p>
          </div>
        )}

        <Button
          onClick={handleLogin}
          variant="outline"
          className="h-14 w-full rounded-xl"
          aria-label="Sign in with Whop"
        >
          <img
            src="/brand/whop-wordmark-dark.png"
            alt="Whop"
            className="h-7 w-auto block dark:hidden"
          />
          <img
            src="/brand/whop-wordmark-light.png"
            alt="Whop"
            className="h-7 w-auto hidden dark:block"
          />
        </Button>
      </div>
    </div>
  );
}

export function Login() {
  return (
    <Suspense
      fallback={
        <div className="flex min-h-[calc(100vh-8rem)] items-center justify-center">
          <p className="text-muted-foreground">Loading...</p>
        </div>
      }
    >
      <LoginContent />
    </Suspense>
  );
}
